import React, { useMemo, useState } from 'react';
import { Table, Icon, Button, Tooltip, Card, Row, Col, Badge, Dropdown, Menu, message, Modal, Radio, DatePicker } from 'antd';
import * as XLSX from 'xlsx';

const { RangePicker } = DatePicker;
const { Group: RadioGroup } = Radio;

const tableStyles = `
@keyframes mb-float  { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-4px)} }
@keyframes mb-beat   { 0%,100%{transform:scale(1)} 50%{transform:scale(1.22)} }
@keyframes mb-bounce { 0%,100%{transform:translateY(0) scaleY(1)} 40%{transform:translateY(-5px) scaleY(1.05)} 70%{transform:translateY(2px) scaleY(0.96)} }
@keyframes mb-shake  { 0%,100%{transform:rotate(0)} 20%{transform:rotate(-14deg)} 40%{transform:rotate(14deg)} 60%{transform:rotate(-8deg)} 80%{transform:rotate(8deg)} }
@keyframes mb-glow   { 0%,100%{box-shadow:0 0 0 0 var(--gc,rgba(24,144,255,.35))} 50%{box-shadow:0 0 0 8px rgba(0,0,0,0)} }
@keyframes mb-spin   { 0%{transform:rotate(0)} 100%{transform:rotate(180deg)} }
@keyframes mb-btn-pulse { 0% { transform: scale(1); } 50% { transform: scale(1.05); box-shadow: 0 4px 15px rgba(82,196,26,0.4); } 100% { transform: scale(1); } }
@keyframes mb-btn-shine { 0% { background-position: -100% 0; } 100% { background-position: 200% 0; } }
@keyframes mb-btn-bounce { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-3px); } }
@keyframes mb-btn-rotate { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }

.mb-float{animation:mb-float 3s ease-in-out infinite}
.mb-beat{animation:mb-beat 1.8s ease-in-out infinite}
.mb-bounce{animation:mb-bounce 2.2s ease-in-out infinite}
.mb-shake{animation:mb-shake 2.6s ease-in-out infinite}
.mb-glow{animation:mb-glow 2.4s ease-in-out infinite}

.mb-row-stripe td{background:#f7f9ff!important}

/* PR/PO badges */
.mb-with  { display:inline-flex;align-items:center;gap:4px;padding:2px 8px;border-radius:8px;font-size:11px;font-weight:700;background:#f6ffed;border:1px solid #b7eb8f;color:#389e0d; }
.mb-with .mb-dot  { width:5px;height:5px;border-radius:50%;background:#52c41a;box-shadow:0 0 5px #52c41a; }
.mb-without { display:inline-flex;align-items:center;gap:4px;padding:2px 8px;border-radius:8px;font-size:11px;font-weight:700;background:#fafafa;border:1px solid #d9d9d9;color:#8c8c8c; }
.mb-without .mb-dot { width:5px;height:5px;border-radius:50%;background:#d9d9d9; }

/* Settings gear */
.mb-settings-btn{width:28px!important;height:28px!important;min-width:28px!important;padding:0!important;border-radius:7px!important;display:inline-flex!important;align-items:center!important;justify-content:center!important;font-size:13px!important;border:1.5px solid #d6e8ff!important;background:#e6f7ff!important;color:#1890ff!important;transition:all .18s!important}
.mb-settings-btn.open{background:#1890ff!important;color:#fff!important;border-color:#1890ff!important;box-shadow:0 3px 10px rgba(24,144,255,.35)!important}
.mb-settings-btn.open .anticon-setting{animation:mb-spin .4s cubic-bezier(.22,1,.36,1) forwards!important}
.mb-action-menu.ant-dropdown-menu{border-radius:10px!important;border:1px solid #e6f0ff!important;box-shadow:0 8px 28px rgba(24,144,255,.12)!important;padding:5px!important;min-width:140px!important}
.mb-action-menu .ant-dropdown-menu-item{border-radius:7px!important;padding:8px 12px!important;font-size:12px!important;font-weight:600!important;transition:background .15s,transform .12s!important}
.mb-action-menu .ant-dropdown-menu-item:hover{transform:translateX(3px)!important;background:#e6f7ff!important;color:#1890ff!important}

/* Export Button Hover Animations */
.mb-export-btn {
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.mb-export-btn:hover {
  transform: translateY(-2px) scale(1.02);
  box-shadow: 0 6px 20px rgba(82,196,26,0.4) !important;
  background: linear-gradient(135deg, #52c41a 0%, #73d13d 100%) !important;
}

.mb-export-btn:active {
  transform: translateY(0) scale(0.98);
}

.mb-export-btn .anticon {
  transition: transform 0.3s ease;
}

.mb-export-btn:hover .anticon {
  animation: mb-btn-rotate 0.6s ease-in-out;
}

/* Shine effect on hover */
.mb-export-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
  transition: left 0.5s ease;
}

.mb-export-btn:hover::before {
  left: 100%;
}

/* Ripple effect */
.mb-export-btn::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(255,255,255,0.5);
  transform: translate(-50%, -50%);
  transition: width 0.6s, height 0.6s;
}

.mb-export-btn:active::after {
  width: 300px;
  height: 300px;
  opacity: 0;
}
`;

const ActionCell = ({ data, openModal }) => {
  const [open, setOpen] = useState(false);
  const menu = (
    <Menu className="mb-action-menu">
      <Menu.Item key="view" onClick={() => openModal(data)}>
        <Icon type="profile" style={{ marginRight: 8, color: '#1890ff' }} />View J.O Details
      </Menu.Item>
    </Menu>
  );
  return (
    <Dropdown overlay={menu} trigger={['hover']} placement="bottomLeft"
      getPopupContainer={() => document.body} onVisibleChange={v => setOpen(v)}>
      <Button className={`mb-settings-btn${open ? ' open' : ''}`} size="small" icon="setting" />
    </Dropdown>
  );
};

const PrPoBadge = ({ has, withLabel, withoutLabel }) => (
  has
    ? <span className="mb-with"><span className="mb-dot" />{withLabel}</span>
    : <span className="mb-without"><span className="mb-dot" />{withoutLabel}</span>
);

const MainboardTable = ({ joList, joListLength, paginate, onPageChange, onSizeChange, openModal }) => {

  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [exportType, setExportType] = useState('all'); // 'all' or 'dateRange'
  const [dateRange, setDateRange] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);

  const stats = useMemo(() => {
    if (!joList || !joList.length) return { total: 0, withPr: 0, withPo: 0, withoutPr: 0, withoutPo: 0, customers: 0 };
    const withPr = joList.filter(j => j.hasPr).length;
    const withPo = joList.filter(j => j.hasPo).length;
    const withoutPr = joList.filter(j => !j.hasPr).length;
    const withoutPo = joList.filter(j => !j.hasPo).length;
    const customers = new Set(joList.map(j => j.customer).filter(Boolean)).size;
    return { total: joList.length, withPr, withPo, withoutPr, withoutPo, customers };
  }, [joList]);

  // Function to add auto-filter to worksheet
  const addAutoFilter = (ws, range) => {
    if (!range) return;
    const ref = XLSX.utils.encode_range(range);
    ws['!autofilter'] = { ref };
  };

  // Excel Export Function with filters
  const exportToExcel = () => {
    setExportLoading(true);
    try {
      let filteredData = [...joList];

      // Filter by date range if selected
      if (exportType === 'dateRange' && dateRange && dateRange[0] && dateRange[1]) {
        const startDate = dateRange[0].startOf('day');
        const endDate = dateRange[1].endOf('day');
        
        filteredData = joList.filter(item => {
          if (!item.date_needed) return false;
          const itemDate = new Date(item.date_needed);
          return itemDate >= startDate.toDate() && itemDate <= endDate.toDate();
        });
      }

      if (filteredData.length === 0) {
        message.warning('No data found for the selected criteria');
        setExportLoading(false);
        setExportModalVisible(false);
        return;
      }

      // Prepare data for export
      const exportData = filteredData.map(item => ({
        'JOB ORDER': item.jo_num || '',
        'CODE': item.code || '',
        'ITEM DESCRIPTION': item.itemDesc || '',
        'QTY': item.quantity || '',
        'CUSTOMER': item.customer || '',
        "CUSTOMER'S P.O": item.poNum || '',
        'DATE NEEDED': item.date_needed || '',
        'PR STATUS': item.hasPr ? 'WITH PR' : 'NO PR',
        'PO STATUS': item.hasPo ? 'WITH PO' : 'NO PO'
      }));

      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);
      
      // Get the range for auto-filter (A1 to last column, header row + data)
      const lastCol = String.fromCharCode(64 + Object.keys(exportData[0] || {}).length);
      const lastRow = exportData.length + 1;
      const filterRange = { s: { r: 0, c: 0 }, e: { r: lastRow - 1, c: Object.keys(exportData[0] || {}).length - 1 } };
      
      // Add auto-filter to all columns
      addAutoFilter(ws, filterRange);

      // Adjust column widths
      const colWidths = [
        { wch: 20 }, // JOB ORDER
        { wch: 15 }, // CODE
        { wch: 45 }, // ITEM DESCRIPTION
        { wch: 10 }, // QTY
        { wch: 25 }, // CUSTOMER
        { wch: 25 }, // CUSTOMER'S P.O
        { wch: 15 }, // DATE NEEDED
        { wch: 15 }, // PR STATUS
        { wch: 15 }  // PO STATUS
      ];
      ws['!cols'] = colWidths;

      // Style the header row
      const range = XLSX.utils.decode_range(ws['!ref']);
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const address = XLSX.utils.encode_col(C) + '1';
        if (!ws[address]) continue;
        ws[address].s = {
          font: { bold: true, sz: 12, color: { rgb: "FFFFFF" } },
          fill: { fgColor: { rgb: "1890FF" }, patternType: "solid" },
          alignment: { horizontal: "center", vertical: "center" },
          border: {
            top: { style: "thin", color: { rgb: "D9D9D9" } },
            bottom: { style: "thin", color: { rgb: "D9D9D9" } },
            left: { style: "thin", color: { rgb: "D9D9D9" } },
            right: { style: "thin", color: { rgb: "D9D9D9" } }
          }
        };
      }

      // Style data rows with borders
      for (let R = 1; R < range.e.r; R++) {
        for (let C = range.s.c; C <= range.e.c; C++) {
          const address = XLSX.utils.encode_cell({ r: R, c: C });
          if (!ws[address]) continue;
          ws[address].s = {
            alignment: { horizontal: "center", vertical: "center" },
            border: {
              top: { style: "thin", color: { rgb: "E0E0E0" } },
              bottom: { style: "thin", color: { rgb: "E0E0E0" } },
              left: { style: "thin", color: { rgb: "E0E0E0" } },
              right: { style: "thin", color: { rgb: "E0E0E0" } }
            }
          };
        }
      }

      // Freeze header row
      ws['!freeze'] = { xSplit: '0', ySplit: '1' };

      // Create workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Job Orders');
      
      // Add summary sheet with filter info
      const summaryData = [
        { 'METRIC': 'EXPORT INFORMATION', 'VALUE': '' },
        { 'METRIC': 'Export Type', 'VALUE': exportType === 'all' ? 'All Data' : 'Date Range Filtered' },
        ...(exportType === 'dateRange' && dateRange ? [
          { 'METRIC': 'Date From', 'VALUE': dateRange[0].format('YYYY-MM-DD') },
          { 'METRIC': 'Date To', 'VALUE': dateRange[1].format('YYYY-MM-DD') },
        ] : []),
        { 'METRIC': '', 'VALUE': '' },
        { 'METRIC': 'SUMMARY STATISTICS', 'VALUE': '' },
        { 'METRIC': 'Total Exported Records', 'VALUE': filteredData.length },
        { 'METRIC': 'TOTAL JOB ORDERS', 'VALUE': stats.total },
        { 'METRIC': 'TOTAL CUSTOMERS', 'VALUE': stats.customers },
        { 'METRIC': 'WITH PR', 'VALUE': stats.withPr },
        { 'METRIC': 'NO PR', 'VALUE': stats.withoutPr },
        { 'METRIC': 'WITH PO', 'VALUE': stats.withPo },
        { 'METRIC': 'NO PO', 'VALUE': stats.withoutPo }
      ];
      
      const wsSummary = XLSX.utils.json_to_sheet(summaryData);
      wsSummary['!cols'] = [{ wch: 30 }, { wch: 25 }];
      
      // Style summary header
      const summaryRange = XLSX.utils.decode_range(wsSummary['!ref']);
      for (let C = summaryRange.s.c; C <= summaryRange.e.c; ++C) {
        for (let R = summaryRange.s.r; R <= summaryRange.e.r; R++) {
          const address = XLSX.utils.encode_cell({ r: R, c: C });
          if (!wsSummary[address]) continue;
          if (summaryData[R] && (summaryData[R]['METRIC'] === 'EXPORT INFORMATION' || summaryData[R]['METRIC'] === 'SUMMARY STATISTICS')) {
            wsSummary[address].s = {
              font: { bold: true, sz: 12, color: { rgb: "FFFFFF" } },
              fill: { fgColor: { rgb: "52C41A" }, patternType: "solid" }
            };
          }
        }
      }
      
      XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');
      
      // Generate filename with current date and filter info
      const date = new Date();
      let filename = `Job_Orders_${date.getFullYear()}-${(date.getMonth()+1).toString().padStart(2,'0')}-${date.getDate().toString().padStart(2,'0')}`;
      if (exportType === 'dateRange' && dateRange) {
        filename += `_${dateRange[0].format('YYYY-MM-DD')}_to_${dateRange[1].format('YYYY-MM-DD')}`;
      }
      filename += '.xlsx';
      
      // Export file
      XLSX.writeFile(wb, filename);
      message.success(`Successfully exported ${filteredData.length} job orders to Excel!`);
      setExportModalVisible(false);
      setDateRange(null);
      setExportType('all');
    } catch (error) {
      console.error('Export error:', error);
      message.error('Failed to export data to Excel');
    } finally {
      setExportLoading(false);
    }
  };

  const cardStyle = { borderRadius: 10, boxShadow: '0 2px 12px rgba(24,144,255,0.08)', border: '1px solid #e6f0ff', background: '#fff', height: '100%' };

  const statCards = [
    { title: 'Total J.O',  value: stats.total,     icon: 'file-text',  color: '#1890ff', bg: '#e6f7ff', anim: 'mb-float',  gc: 'rgba(24,144,255,0.3)' },
    { title: 'Customers',  value: stats.customers, icon: 'team',       color: '#13c2c2', bg: '#e6fffb', anim: 'mb-beat',   gc: 'rgba(19,194,194,0.3)' },
    { 
      title: 'PR Status',  
      value: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
            <span style={{ fontSize: '12px', color: '#52c41a' }}>With PR:</span>
            <span style={{ fontSize: '18px', fontWeight: 700, color: '#52c41a' }}>{stats.withPr}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
            <span style={{ fontSize: '12px', color: '#ff4d4f' }}>NO PR:</span>
            <span style={{ fontSize: '18px', fontWeight: 700, color: '#ff4d4f' }}>{stats.withoutPr}</span>
          </div>
        </div>
      ),
      icon: 'file-done',  
      color: '#52c41a', 
      bg: '#f6ffed', 
      anim: 'mb-bounce', 
      gc: 'rgba(82,196,26,0.3)',
      multiValue: true
    },
    { 
      title: 'PO Status',  
      value: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
            <span style={{ fontSize: '12px', color: '#722ed1' }}>With PO:</span>
            <span style={{ fontSize: '18px', fontWeight: 700, color: '#722ed1' }}>{stats.withPo}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
            <span style={{ fontSize: '12px', color: '#ff4d4f' }}>NO PO:</span>
            <span style={{ fontSize: '18px', fontWeight: 700, color: '#ff4d4f' }}>{stats.withoutPo}</span>
          </div>
        </div>
      ),
      icon: 'shopping-cart', 
      color: '#722ed1', 
      bg: '#f9f0ff', 
      anim: 'mb-shake', 
      gc: 'rgba(114,46,209,0.3)',
      multiValue: true
    },
  ];

  const columns = [
    {
      key: 'actions', title: '', align: 'center', width: 46,
      render: data => <ActionCell data={data} openModal={openModal} />,
    },
    {
      key: 'jo_num', dataIndex: 'jo_num', title: 'JOB ORDER', align: 'center',
      render: val => <span style={{ fontWeight: 700, color: '#262626' }}>{val}</span>,
    },
    { key: 'code',     dataIndex: 'code',     title: 'CODE',             align: 'center' },
    { key: 'itemDesc', dataIndex: 'itemDesc', title: 'ITEM DESCRIPTION', align: 'center' },
    {
      key: 'servedQty', title: 'QTY', align: 'center', dataIndex: 'quantity',
      render: val => (
        <span style={{ padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 700, background: '#e6f7ff', border: '1px solid #91d5ff', color: '#1890ff' }}>
          {val}
        </span>
      ),
    },
    { key: 'customer', dataIndex: 'customer', title: 'CUSTOMER', align: 'center' },
    { key: 'poNum',    dataIndex: 'poNum',    title: "CUSTOMER'S P.O", align: 'center' },
    {
      key: 'date_needed', dataIndex: 'date_needed', title: 'DATE NEEDED', align: 'center',
      render: val => val ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#595959' }}>
          <Icon type="calendar" style={{ color: '#1890ff', fontSize: 11 }} />{val}
        </span>
      ) : <Icon type="ellipsis" style={{ color: '#d9d9d9' }} />,
    },
    {
      key: 'pr', title: 'PR', align: 'center',
      render: data => <PrPoBadge has={data.hasPr} withLabel="WITH PR" withoutLabel="NO PR" />,
      filters: [{ text: 'WITH PR', value: true }, { text: 'NO PR', value: false }],
      onFilter: (value, record) => !!record.hasPr === value,
    },
    {
      key: 'po', title: 'PO', align: 'center',
      render: data => <PrPoBadge has={data.hasPo} withLabel="WITH PO" withoutLabel="NO PO" />,
      filters: [{ text: 'WITH PO', value: true }, { text: 'NO PO', value: false }],
      onFilter: (value, record) => !!record.hasPo === value,
    },
  ];

  return (
    <div style={{ padding: '8px 0' }}>
      <style>{tableStyles}</style>

      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        {statCards.map((card, idx) => (
          <Col key={idx} xs={12} sm={6}>
            <Card style={cardStyle} bodyStyle={{ padding: '18px 20px' }} bordered={false}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div className={`mb-glow ${card.anim}`}
                  style={{ width: 46, height: 46, borderRadius: 12, background: card.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, '--gc': card.gc }}>
                  <Icon type={card.icon} style={{ fontSize: 20, color: card.color }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, color: '#8c8c8c', fontWeight: 500, letterSpacing: '.3px', whiteSpace: 'nowrap' }}>{card.title.toUpperCase()}</div>
                  {card.multiValue ? (
                    card.value
                  ) : (
                    <div style={{ fontSize: 24, fontWeight: 700, color: card.color, lineHeight: 1.2 }}>{card.value}</div>
                  )}
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Card bordered={false}
        style={{ borderRadius: 10, boxShadow: '0 2px 12px rgba(24,144,255,0.07)', border: '1px solid #e6f0ff' }}
        bodyStyle={{ padding: '0 0 8px 0' }}>
        <div style={{ padding: '14px 20px 12px', borderBottom: '1px solid #f0f5ff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon type="reconciliation" className="mb-beat" style={{ color: '#1890ff', fontSize: 16 }} />
            <span style={{ fontWeight: 700, fontSize: 13, color: '#262626', letterSpacing: '.5px' }}>JOB ORDER MAINBOARD</span>
            <Badge count={joListLength || stats.total} style={{ backgroundColor: '#1890ff', fontSize: 11, fontWeight: 600 }} overflowCount={99999} />
          </div>
          <Button 
            type="primary" 
            icon="export" 
            onClick={() => setExportModalVisible(true)}
            className="mb-export-btn"
            style={{ 
              borderRadius: 8, 
              background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
              borderColor: '#52c41a',
              boxShadow: '0 2px 8px rgba(82,196,26,0.25)',
              height: 36,
              fontSize: 13,
              fontWeight: 600,
              letterSpacing: '0.5px',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            Export to Excel
          </Button>
        </div>
        <Table
          bodyStyle={{ overflowX: 'auto' }}
          columns={columns}
          rowKey="id"
          dataSource={joList}
          size="middle"
          style={{ margin: 0 }}
          rowClassName={(_, idx) => idx % 2 === 0 ? '' : 'mb-row-stripe'}
          pagination={{
            total: joListLength,
            current: paginate.page,
            pageSize: paginate.pageSize,
            defaultPageSize: paginate.pageSize,
            showSizeChanger: true,
            onChange: onPageChange,
            onShowSizeChange: onSizeChange,
            pageSizeOptions: ['10', '25', '50', '100', '500'],
            showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} items`,
            position: 'both',
            style: { padding: '8px 16px', marginBottom: 0 },
          }}
          bordered
        />
      </Card>

      {/* Export Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Icon type="export" style={{ color: '#52c41a', fontSize: 20 }} />
            <span style={{ fontWeight: 600, fontSize: 16 }}>Export Job Orders to Excel</span>
          </div>
        }
        visible={exportModalVisible}
        onOk={exportToExcel}
        onCancel={() => {
          setExportModalVisible(false);
          setDateRange(null);
          setExportType('all');
        }}
        confirmLoading={exportLoading}
        okText="Export"
        cancelText="Cancel"
        okButtonProps={{ 
          style: { background: '#52c41a', borderColor: '#52c41a', borderRadius: 8 },
          icon: 'download'
        }}
        cancelButtonProps={{ style: { borderRadius: 8 } }}
        width={500}
        style={{ top: 150 }}
        bodyStyle={{ padding: '24px' }}
      >
        <div style={{ marginBottom: 20 }}>
          <RadioGroup 
            onChange={(e) => setExportType(e.target.value)} 
            value={exportType}
            style={{ width: '100%', marginBottom: 16 }}
          >
            <Radio value="all" style={{ display: 'block', marginBottom: 12 }}>
              <strong>Export All Data</strong>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
                Export all job orders without date filtering
              </div>
            </Radio>
            <Radio value="dateRange" style={{ display: 'block' }}>
              <strong>Export by Date Range</strong>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
                Filter job orders based on DATE NEEDED
              </div>
            </Radio>
          </RadioGroup>

          {exportType === 'dateRange' && (
            <div style={{ 
              marginTop: 16, 
              padding: '16px', 
              background: '#f9f9f9', 
              borderRadius: 8,
              border: '1px solid #e8e8e8'
            }}>
              <div style={{ marginBottom: 8, fontWeight: 500, color: '#1890ff' }}>
                <Icon type="calendar" /> Select Date Range:
              </div>
              <RangePicker 
                style={{ width: '100%' }}
                onChange={(dates) => setDateRange(dates)}
                placeholder={['Start Date', 'End Date']}
                format="YYYY-MM-DD"
              />
              <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 8 }}>
                * Only job orders with DATE NEEDED within this range will be exported
              </div>
            </div>
          )}
        </div>

        <div style={{ 
          padding: '12px', 
          background: '#e6f7ff', 
          borderRadius: 8,
          border: '1px solid #91d5ff',
          fontSize: 12
        }}>
          <Icon type="info-circle" style={{ color: '#1890ff', marginRight: 8 }} />
          <span style={{ color: '#0050b3' }}>
            The exported Excel file will have:
            <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
              <li>Auto-filter enabled on all columns</li>
              <li>Styled header row with blue background</li>
              <li>Borders for better readability</li>
              <li>Separate summary sheet with statistics</li>
            </ul>
          </span>
        </div>
      </Modal>
    </div>
  );
};

export default MainboardTable;